import type { Category, NeedsYou } from './useNeedsYou'

/**
 * The shell's "waiting on you" queue, narrowed to the categories ONE banner describes.
 *
 * Ledger's waiting banner describes questions and escalations (held drafts are its Shadow
 * banner's); Knowledge's Inbox banner describes questions alone ("N questions she brought
 * back"). Each must state its OWN honest state rather than inherit another category's
 * failure, so the narrowing is one function both call — not two hand-copied filters.
 *
 * ⚠️ The honest states, scoped to `categories`:
 *  · `degraded` — a read for one of them failed: the count is "at the last check";
 *  · `unknown`  — one of them failed and has never loaded: "there may be more";
 *  · `incomplete` — degraded, or one of them is still in flight: "so far".
 * A queue that has not been read, or could not be, never reads as an empty one.
 */
export interface QueueScope {
  /** Items in these categories. A floor, not a count, whenever `incomplete`. */
  total: number
  degraded: boolean
  unknown: boolean
  incomplete: boolean
}

type ListCategory = Exclude<Category, 'shadow'>

export function scopeQueue(q: NeedsYou, categories: readonly ListCategory[]): QueueScope {
  const inScope = (c: Category) => (categories as readonly Category[]).includes(c)
  const total = categories.reduce((n, c) => n + q[c].length, 0)
  const degraded = q.stale.some(inScope)
  return {
    total,
    degraded,
    unknown: q.unknownIn.some(inScope),
    incomplete: degraded || q.pending.some(inScope),
  }
}
